import React, { useMemo } from 'react';
import { Button, Space, Tooltip, Typography } from 'antd';
import { useIntl } from 'umi';
import { CalendarOutlined, MailOutlined, PhoneOutlined } from '@ant-design/icons';
import { CustomerInfo } from '../types';
import styles from './CustomerHeaderCard.less';
import LeadName from '@/components/Lead/LeadName';
import { LeadDetailsModel } from '@/typings/models/Lead';
import PhoneNumberFormat from '@/components/format/PhoneNumberFormat';
import { F } from '@/utils/intl';
import { CUSTOMER_TYPES } from '@/typings/models/Contact';
import { getContactName, getHouseholdLabel, isHousehold } from '@/utils/contacts';

const { Text } = Typography;

export interface CustomerHeaderCardProps {
  customer: CustomerInfo;
  lead: LeadDetailsModel;
  onEmail?: () => void;
  onCall?: () => void;
  onSchedule?: () => void;
  onPrint?: () => void;
}

const CustomerHeaderCard: React.FC<CustomerHeaderCardProps> = ({
                                                                 customer,
                                                                 lead,
                                                                 onEmail,
                                                                 onCall,
                                                                 onSchedule,
                                                               }) => {
  const intl = useIntl();
  // Icon-only actions: the name goes to the tooltip and to screen readers.
  const labels = {
    email: intl.formatMessage({ id: 'email.action.new', defaultMessage: 'New email' }),
    call: intl.formatMessage({ id: 'pages.lead.action.call', defaultMessage: 'Call' }),
    schedule: intl.formatMessage({ id: 'pages.lead.next_action_book', defaultMessage: 'Book Appointment' }),
  };
  const companyName = lead?.customer?.type === CUSTOMER_TYPES.ORGANIZATION ? lead.customer.company_name : null;
  const primaryContact = lead?.primary_contact;
  const householdLabel = isHousehold(lead?.customer, lead?.contacts)
    ? getHouseholdLabel(intl, lead?.customer, lead?.contacts)
    : null;
  // Who the email/phone reach: the project's primary contact, else the customer.
  const reachable = primaryContact || customer;
  // A company is titled by its name; a private customer by the project's primary contact.
  const nameLead = useMemo(() => {
    if (companyName) {
      return { customer: { type: CUSTOMER_TYPES.ORGANIZATION, company_name: companyName, firstname: '', lastname: '' } };
    }
    if (primaryContact) {
      return { customer: { firstname: primaryContact.firstname || '', lastname: primaryContact.lastname || '' } };
    }
    return lead;
  }, [companyName, primaryContact, lead]);
  // A household is titled by its name, the avatar stays the primary contact's.
  const titleLead = useMemo(
    () => (householdLabel ? { customer: { firstname: householdLabel, lastname: '' } } : nameLead),
    [householdLabel, nameLead],
  );

  const subtitle = useMemo(() => {
    const withRole = (name: string, role?: string | null) => (role ? `${name} · ${role}` : name);
    if ((companyName || householdLabel) && primaryContact) {
      return (
        <F
          id="component.contacts.headerPrimary"
          defaultMessage="Primary contact: {name}"
          values={{ name: withRole(getContactName(primaryContact), primaryContact.role) }}
        />
      );
    }
    const others = (lead?.contacts || []).filter((contact) => contact.id !== primaryContact?.id);
    if (companyName || !others.length) return null;
    const more = others.length > 1 ? ` +${others.length - 1}` : '';
    return (
      <F
        id="component.contacts.headerWith"
        defaultMessage="with {name}"
        values={{ name: `${withRole(getContactName(others[0]), others[0].role)}${more}` }}
      />
    );
  }, [companyName, householdLabel, lead?.contacts, primaryContact]);

  return (
    <div className={styles.customerHeaderCard}>
      <div className={styles.headerContent}>
        <div className={styles.customerInfo}>
          <div className={styles.avatarContainer}>
            <LeadName lead={nameLead} justAvatar />
          </div>
          <div className={styles.details}>
            <h2 className={styles.name}>
              {/* The avatar beside it already marks a company. */}
              <LeadName hideAvatar hideCompanyIcon readonly lead={titleLead} />
            </h2>
            {subtitle && <div className={styles.subtitle}>{subtitle}</div>}
            <div className={styles.contactInfo}>
              {reachable?.email && (
                <Text
                  className={styles.copyableText}
                  copyable={{
                    text: reachable.email,
                  }}
                >
                  <div className={styles.contactItem}>
                    <MailOutlined className={styles.icon} />
                    <span>{reachable.email}</span>
                  </div>
                </Text>
              )}
              {reachable?.phone && (
                <Text
                  className={styles.copyableText}
                  copyable={{ text: reachable.phone }}
                >
                  <div className={styles.contactItem}>
                    <PhoneNumberFormat phoneNumber={reachable.phone} variant="link" />
                  </div>
                </Text>
              )}
            </div>
            <div className={styles.actions}>
              <Space>
                {onEmail && reachable?.email && (
                  <Tooltip title={labels.email}>
                    <Button icon={<MailOutlined />} aria-label={labels.email} onClick={onEmail} />
                  </Tooltip>
                )}
                {onCall && reachable?.phone && (
                  <Tooltip title={labels.call}>
                    <Button icon={<PhoneOutlined />} aria-label={labels.call} onClick={onCall} />
                  </Tooltip>
                )}
                {onSchedule && (
                  <Tooltip title={labels.schedule}>
                    <Button icon={<CalendarOutlined />} aria-label={labels.schedule} onClick={onSchedule} />
                  </Tooltip>
                )}
              </Space>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerHeaderCard;
